/**
 * Minimal SVG sparkline component. No deps; theme-token coloured.
 */

import { useRef, useState } from 'react';

export interface SparklineProps {
  values: number[];
  /** Optional single uncertainty band (back-compat). */
  band?: { low: number[]; high: number[] };
  /** Optional stacked uncertainty bands (e.g. 95% then 50%) — drawn widest-first
   *  so a single chart shows the full fan instead of N stacked sparklines. */
  bands?: { low: number[]; high: number[]; opacity?: number }[];
  /** Label rendered top-left. */
  label?: string;
  /** Stroke colour via design token (e.g., 'var(--bm-danger)'). */
  stroke?: string;
  /** Show the latest value as text annotation. */
  showLatest?: boolean;
  /** Show y-axis ticks at min + max. */
  showRange?: boolean;
  height?: number;
  /** Enable hover crosshair (CD-review R3a). When on, the cursor draws a
   *  vertical dashed line + median dot at the nearest data index, with a
   *  read-out line below the SVG showing `day N · median · 95% [low–high]`.
   *  Off by default so the chart still renders quietly inside small cells. */
  interactive?: boolean;
  /** Optional x-axis label formatter for the crosshair read-out (defaults to
   *  `day N`). E.g. `(i) => '2026-W' + i` for ISO-week x-axes. */
  xLabel?: (i: number) => string;
}

export function Sparkline({
  values, band, bands, label,
  stroke = 'var(--bm-accent)',
  showLatest = true,
  showRange = true,
  height = 200,
  interactive = false,
  xLabel,
}: SparklineProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [hi, setHi] = useState<number | null>(null);

  if (values.length === 0) {
    return <div className="sparkline" style={{ height }}>—</div>;
  }
  const w = 800;
  const h = height;
  const padL = 40, padR = 60, padT = 20, padB = 20;
  const plotW = w - padL - padR;
  const plotH = h - padT - padB;

  // Normalise the single `band` + the `bands` array into one list, widest first.
  const allBands = [
    ...(band ? [{ low: band.low, high: band.high, opacity: 0.18 }] : []),
    ...(bands ?? []),
  ];

  const allValues = allBands.length
    ? [...values, ...allBands.flatMap(b => [...b.low, ...b.high])]
    : values;
  const min = Math.min(...allValues, 0);
  const max = Math.max(...allValues);
  const range = max - min || 1;

  const xAt = (i: number): number => padL + (i / (values.length - 1 || 1)) * plotW;
  const yAt = (v: number): number => padT + plotH - ((v - min) / range) * plotH;

  const path = values.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i)} ${yAt(v)}`).join(' ');

  // Each band → a filled polygon (up the high edge, back down the low edge).
  const bandPoly = (b: { low: number[]; high: number[] }): string => {
    const up = b.high.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i)} ${yAt(v)}`).join(' ');
    const down = b.low.map((_, i) => {
      const idx = b.low.length - 1 - i;
      return `L ${xAt(idx)} ${yAt(b.low[idx]!)}`;
    }).join(' ');
    return `${up} ${down} Z`;
  };

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const el = svgRef.current; if (!el) return;
    const rect = el.getBoundingClientRect();
    // Map clientX → SVG viewBox coord (preserveAspectRatio="none" scales x linearly).
    const xInSvg = ((e.clientX - rect.left) / rect.width) * w;
    const t = (xInSvg - padL) / plotW;
    let idx = Math.round(t * (values.length - 1));
    idx = Math.max(0, Math.min(values.length - 1, idx));
    setHi(idx);
  };
  const onLeave = () => setHi(null);
  // For the read-out: prefer the widest band as the 95% PI representation.
  const widest = allBands[0];

  return (
    <div className="sparkline" style={{ height: h }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        style={interactive ? { cursor: 'crosshair' } : undefined}
        onMouseMove={interactive ? onMove : undefined}
        onMouseLeave={interactive ? onLeave : undefined}
      >
        {label && (
          <text x={padL} y={padT - 4}
            fontSize="11" fill="var(--bm-text-muted)" fontFamily="var(--bm-font-mono)">
            {label}
          </text>
        )}
        {showRange && (
          <>
            <text x={padL - 6} y={padT + 4} textAnchor="end"
              fontSize="10" fill="var(--bm-text-subtle)" fontFamily="var(--bm-font-mono)">
              {abbr(max)}
            </text>
            <text x={padL - 6} y={padT + plotH} textAnchor="end"
              fontSize="10" fill="var(--bm-text-subtle)" fontFamily="var(--bm-font-mono)">
              {abbr(min)}
            </text>
          </>
        )}
        {allBands.map((b, i) => (
          <path key={i} d={bandPoly(b)} fill={stroke} opacity={b.opacity ?? 0.18} />
        ))}
        <path d={path} fill="none" stroke={stroke} strokeWidth="1.6" strokeLinejoin="round" />
        {showLatest && (
          <text
            x={xAt(values.length - 1) + 8}
            y={yAt(values[values.length - 1]!)}
            dy={4}
            fontSize="11" fill={stroke} fontFamily="var(--bm-font-mono)" fontWeight="600">
            {abbr(values[values.length - 1]!)}
          </text>
        )}
        {interactive && hi != null && (
          <g pointerEvents="none">
            <line
              x1={xAt(hi)} x2={xAt(hi)}
              y1={padT} y2={padT + plotH}
              stroke="var(--bm-plot-crosshair, var(--bm-text-subtle))"
              strokeWidth="1" strokeDasharray="3 3" opacity="0.7"
            />
            <circle
              cx={xAt(hi)} cy={yAt(values[hi]!)} r="3.5"
              fill={stroke} stroke="var(--bm-bg)" strokeWidth="1.5"
            />
          </g>
        )}
      </svg>
      {interactive && (
        <div style={{
          display: 'flex', justifyContent: 'flex-end',
          marginTop: 4, paddingRight: 4,
          font: '11px var(--bm-font-mono)', color: hi != null ? 'var(--bm-text)' : 'var(--bm-text-subtle)',
        }}>
          {hi != null
            ? `${xLabel ? xLabel(hi) : 'day ' + hi} · median ${abbr(values[hi]!)}`
              + (widest ? ` · 95% [${abbr(widest.low[hi]!)}–${abbr(widest.high[hi]!)}]` : '')
            : 'hover to read the envelope'}
        </div>
      )}
    </div>
  );
}

function abbr(v: number): string {
  if (!Number.isFinite(v)) return '—';
  const sign = v < 0 ? '-' : '';
  const abs = Math.abs(v);
  if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(1)}k`;
  if (abs >= 1)   return `${sign}${abs.toFixed(0)}`;
  if (abs > 0)    return `${sign}${abs.toFixed(2)}`;
  return '0';
}
